import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PlayCircle,
  ArrowRight,
  BookOpen,
  Bot,
  BrainCircuit,
  Building2,
  HeartHandshake,
  CalendarDays,
  CalendarHeart,
  CheckCircle2,
  ClipboardCheck,
  Cloud,
  Fingerprint,
  Code2,
  FlaskConical,
  Gift,
  Globe,
  GraduationCap,
  Heart,
  Megaphone,
  School,
  UsersRound,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Sparkles,
  Swords,
  Bug,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import {
  EVENT_KIND_LABELS,
  LEVEL_LABELS,
  SESSION_FORMAT_LABELS,
  type SessionFormat,
  type Topic,
} from '@ready/shared';
import { useApi, type LibraryEvent } from '@/lib/api';
import { ReadyMark } from '@/components/logo';
import { ThemeToggle } from '@/components/theme-toggle';
import { SiteFooter } from '@/components/footer-links';
import { formatMinutes } from '@/components/catalog';
import { PathCards } from '@/components/paths';
import { formatSessionTime } from '@/lib/dates';
import { OfferedBy } from '@/components/org-brand';
import { VideoPlayer } from '@/components/video';
import { findVideo } from '@/content/videos';

/*
 * The public landing page, brought over from learning.nirvik.ai (Nirvik
 * Learning) with its sections and copy, under the Nirvik Ready name.
 *
 * Tone: warm, inviting, student-first. Dark by default like nirvik.ai,
 * india-green leading the accents, turmeric anchoring the calls to action.
 * The page has its own nav and footer (full width, outside the app layout).
 *
 * Sections: announcement strip, nav, hero, Cybersecurity Awareness Month
 * (October only), upcoming workshops and webinars (when any are scheduled), why now, courses (live from Nirvik's library, then
 * "opening soon" tracks), how learning works, educators, teams, values,
 * call to action, footer.
 */

const START = '/signin';
const EDUCATOR_EMAIL = 'educators@nirvik.ai';
const AWARENESS = {
  course: '/c/spot-the-scam',
  challenge: '/q/cyber-month-2026',
};

/** Cybersecurity Awareness Month runs through October every year. */
const isAwarenessMonth = () => new Date().getMonth() === 9;

export function LandingPage() {
  const awareness = isAwarenessMonth();
  return (
    <div className="min-h-dvh bg-background text-foreground">
      {/* ─── Announcement strip ─── */}
      <a
        href={awareness ? '#awareness' : '#tracks'}
        className="block bg-gradient-to-r from-emerald-800 via-amber-700 to-red-900 px-4 py-2.5 text-center text-xs font-medium text-white transition-opacity hover:opacity-95 sm:text-sm"
      >
        <span className="inline-flex flex-wrap items-center justify-center gap-2">
          {awareness ? (
            <>
              <CalendarHeart className="h-4 w-4 shrink-0" aria-hidden />
              <span>
                <strong>October is Cybersecurity Awareness Month</strong>
                <span className="hidden sm:inline">: take the free 26-question challenge</span>
              </span>
            </>
          ) : (
            <>
              <Gift className="h-4 w-4 shrink-0" aria-hidden />
              <span>
                <strong>Free for every learner</strong>
                <span className="hidden sm:inline">
                  {' '}
                  — open curriculum, interactive labs, and educator tools
                </span>
              </span>
            </>
          )}
          <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </span>
      </a>

      {/* ─── Nav ─── */}
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3.5 sm:px-6 sm:py-4">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <ReadyMark className="h-7 w-7 shrink-0 text-primary-strong sm:h-8 sm:w-8" />
          <span className="truncate font-display text-xl font-semibold tracking-wide text-foreground sm:text-2xl">
            Nirvik <span className="text-india-green">Ready</span>
          </span>
        </Link>
        <div className="hidden items-center gap-7 text-sm text-muted-foreground md:flex">
          <a href="#tracks" className="transition-colors hover:text-foreground">
            Courses
          </a>
          <a href="#how" className="transition-colors hover:text-foreground">
            How it works
          </a>
          <a href="#educators" className="transition-colors hover:text-foreground">
            For educators
          </a>
          <Link to="/resources" className="transition-colors hover:text-foreground">
            Resources
          </Link>
          <a href="https://nirvik.ai" className="transition-colors hover:text-foreground">
            Nirvik AI Labs
          </a>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <Link
            to={START}
            className="hidden px-2 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground sm:inline"
          >
            Sign in
          </Link>
          <Link
            to={START}
            className="whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 sm:px-4"
          >
            Start Learning
            <span className="hidden sm:inline"> Free</span>
          </Link>
          <ThemeToggle />
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative mx-auto max-w-4xl overflow-hidden px-4 py-16 text-center sm:px-6 md:py-24">
        <div className="pointer-events-none absolute inset-0 -z-10 flex items-start justify-center">
          <div className="h-[450px] w-[650px] bg-halo-green opacity-70" />
        </div>

        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-india-green/30 bg-india-green/12 px-3 py-1 text-xs font-medium text-india-green sm:text-sm">
          <Heart className="h-4 w-4 shrink-0" aria-hidden />
          Free. Open. For every builder.
        </div>
        <h1 className="mb-6 font-display text-4xl leading-[1.05] font-semibold text-foreground sm:text-5xl md:text-6xl">
          Cyber Security Fluency
          <br />
          <span className="text-india-green">for the next billion builders</span>
        </h1>
        <p className="mx-auto mb-10 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-xl">
          An interactive curriculum that teaches cybersecurity and makes it accessible to every kind
          of builder on the planet.
        </p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <Link
            to={START}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-base font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90 sm:px-6 sm:text-lg"
          >
            Start Learning Free
            <ArrowRight className="h-5 w-5 shrink-0" aria-hidden />
          </Link>
          <a
            href="#tracks"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-secondary px-5 py-3 text-base font-medium text-foreground transition-colors hover:bg-accent sm:px-6 sm:text-lg"
          >
            Browse courses
          </a>
        </div>

        <a
          href="#overview"
          className="mt-6 inline-flex items-center gap-2 py-2 text-sm font-semibold text-primary-strong hover:underline"
        >
          <PlayCircle className="h-4 w-4 shrink-0" aria-hidden />
          Watch the 4-minute overview
        </a>
        <br />
        <p className="mt-8 inline-flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-india-green" aria-hidden />
          Free forever · Open curriculum · For every builder
        </p>
      </section>

      {awareness && <AwarenessMonth />}

      <UpcomingEvents />

      {/* ─── Why now ─── */}
      <section className="bg-muted/50 px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-6 text-center text-xs font-bold tracking-[0.24em] text-india-green uppercase">
            Why now
          </h2>
          <p className="mb-8 text-center font-display text-2xl leading-tight font-semibold text-foreground sm:text-3xl md:text-4xl">
            The next billion lines of code{' '}
            <span className="text-muted-foreground/70">
              will be generated by builders who have never written a single line of code before.
            </span>
          </p>
          <p className="text-center text-base leading-relaxed text-muted-foreground sm:text-lg">
            Security fluency is now a foundational skill for every builder — the practitioner, the
            student, the vibe coder. We're building a curriculum that meets people where they are:
            bite-sized, hands-on, paired with an AI tutor that explains until it clicks. Because the
            builders generating tomorrow's software are the ones who will shape how secure it is.
          </p>
        </div>
      </section>

      <PitchVideo />

      <StartingPoints />

      <Courses />

      {/* ─── How learning works ─── */}
      <section id="how" className="bg-muted/50 px-4 py-16 sm:px-6 md:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-3 text-center font-display text-2xl font-semibold text-foreground sm:text-3xl md:text-4xl">
            How learning works here
          </h2>
          <p className="mx-auto mb-14 max-w-2xl text-center text-base text-muted-foreground sm:text-lg">
            Designed around how humans actually learn — in context, at their own pace, with
            immediate feedback.
          </p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                icon: BookOpen,
                title: 'Plain-language lessons',
                description:
                  'Every concept explained like a mentor would. 5–12 minutes each. Read, watch, or listen.',
                iconColor: 'text-india-green',
              },
              {
                icon: FlaskConical,
                title: 'Hands-on labs',
                description:
                  'Work with real tools in live sandboxes. Apply what you learn, see the results firsthand, and build intuition through practice.',
                iconColor: 'text-primary-strong',
              },
              {
                icon: Sparkles,
                title: 'An AI tutor that never gets tired',
                description:
                  'Ask anything in your own words. The tutor knows the lesson, sees your work, and keeps explaining until it clicks.',
                iconColor: 'text-chakra-navy',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-border bg-card p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
                  <item.icon className={`h-6 w-6 ${item.iconColor}`} aria-hidden />
                </div>
                <h3 className="mb-2 font-display text-lg font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── For educators ─── */}
      <AudienceSection
        id="educators"
        card="border-chakra-navy/25 from-chakra-navy/5 to-india-green/5"
        chip="border-chakra-navy/25 bg-chakra-navy/10 text-chakra-navy"
        linkColor="text-chakra-navy"
        icon={School}
        kicker="For educators"
        title="Bring Nirvik Ready to your campus"
        video="pitch-universities"
        guide={{ to: '/guides/universities', label: 'Guide for universities and schools' }}
        action={
          <ContactButton
            href={`mailto:${EDUCATOR_EMAIL}`}
            label="Contact educators team"
            className="bg-chakra-navy text-chakra-navy-foreground"
          />
        }
      >
        <p className="mb-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Free curriculum for every accredited university, community college, and non-profit
          bootcamp. Classroom dashboards, assignment grading, and private cohort spaces — no
          per-seat pricing, ever.
        </p>
        <Bullets
          color="text-india-green"
          items={[
            'Ready-to-teach lesson plans for semester or bootcamp formats',
            'Private cohort spaces with instructor-only views',
            'Automatic grading on hands-on labs, with human-style feedback',
            'Custom tracks — we help you build what your program needs',
          ]}
        />
      </AudienceSection>

      {/* ─── For non-profits ─── */}
      <AudienceSection
        id="nonprofits"
        card="border-india-green/25 from-india-green/5 to-primary/5"
        chip="border-india-green/25 bg-india-green/10 text-india-green"
        linkColor="text-india-green"
        icon={HeartHandshake}
        kicker="For non-profits"
        title="Protect the people you serve"
        video="pitch-nonprofits"
        guide={{ to: '/guides/nonprofits', label: 'Guide for non-profits' }}
        action={
          <ContactButton
            href="mailto:non-profits@nirvik.ai"
            label="Contact non-profits team"
            className="bg-india-green text-india-green-foreground"
          />
        }
      >
        <p className="mb-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
          The people non-profits serve are often the people scammers target first. Free courses,
          workshops and certificates help you protect your community and train your staff and
          volunteers, without a training budget.
        </p>
        <Bullets
          color="text-india-green"
          items={[
            'Scam-safety courses for families, seniors and new immigrants, on any phone',
            'In-person workshops at your center, with registration, reminders and check-in',
            'Volunteer onboarding in one invite link, with assigned courses and reminders',
            'Chapters that run their own programs, and reports for your funders',
          ]}
        />
      </AudienceSection>

      {/* ─── For teams ─── */}
      <AudienceSection
        id="teams"
        card="border-saffron/25 from-saffron/5 to-primary/5"
        chip="border-saffron/25 bg-saffron/10 text-saffron"
        linkColor="text-saffron"
        icon={Building2}
        kicker="For teams"
        title="Upskill your team for the age of agentic AI"
        video="pitch-companies"
        guide={{ to: '/guides/companies', label: 'Guide for companies and teams' }}
        action={
          <ContactButton
            href="mailto:teams@nirvik.ai"
            label="Talk to us"
            className="bg-saffron text-background"
          />
        }
      >
        <p className="mb-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Focused workshops for engineering and product teams at SMBs and enterprises. Build the
          skills your team needs to work confidently with AI agents, MCP-connected tools, and the
          new generation of AI-assisted development workflows — with security built in from day one.
        </p>
        <Bullets
          color="text-saffron"
          items={[
            'Working with AI agents and multi-agent systems — safely and effectively',
            'Leveraging MCP to connect AI to your tools, data, and infrastructure',
            'Securing AI-assisted development workflows across your team',
            "Custom workshops scoped to your stack, your tools, and your team's level",
          ]}
        />
        <p className="mb-6 text-xs text-muted-foreground/60">
          Teams can choose to pay a nominal fee to support the free-for-everyone mission of Nirvik
          Ready. We love to support teams of all sizes, from startups to enterprises.
        </p>
      </AudienceSection>

      {/* ─── For communities ─── */}
      <AudienceSection
        id="communities"
        card="border-primary/25 from-primary/5 to-chakra-navy/5"
        chip="border-primary/25 bg-primary/10 text-primary-strong"
        linkColor="text-primary-strong"
        icon={UsersRound}
        kicker="For communities"
        title="Give every meetup something to show for it"
        video="pitch-communities"
        guide={{ to: '/guides/communities', label: 'Guide for communities and meetups' }}
        action={
          <ContactButton
            href="mailto:communities@nirvik.ai"
            label="Contact communities team"
            className="bg-primary text-primary-foreground"
          />
        }
      >
        <p className="mb-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Meetups, user groups and clubs run on volunteers and enthusiasm. Nirvik Ready gives your
          community the tools of a training program, for free.
        </p>
        <Bullets
          color="text-primary-strong"
          items={[
            'Quiz challenges to open every meetup, answered on members’ phones',
            'Events with registration, waitlists, check-in codes and certificates',
            'Your public events on Nirvik Ready’s home page, hosted by you',
            'City chapters with their own organizers, and invite posters with a QR code',
          ]}
        />
      </AudienceSection>

      {/* ─── Community / values ─── */}
      <section className="bg-muted/40 px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-6 font-display text-2xl leading-tight font-semibold text-foreground sm:text-3xl md:text-4xl">
            Security should be a public good.
          </h2>
          <p className="mx-auto max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            The internet is built on a shared infrastructure — its safety has to be a shared project
            too. Nirvik Ready will stay free for individuals and schools, forever. Funded by the
            Trinetra platform; stewarded by Nirvik AI Labs.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3 sm:gap-4">
            {[
              { icon: Bug, label: '120+ vulnerabilities covered' },
              { icon: Users, label: 'Open to every learner' },
              { icon: GraduationCap, label: 'Partnered with universities' },
            ].map((item) => (
              <div
                key={item.label}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground"
              >
                <item.icon className="h-4 w-4 text-india-green" aria-hidden />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Call to action ─── */}
      <section className="px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="mb-4 font-display text-3xl font-semibold text-foreground sm:text-4xl">
            Ready to start?
          </h2>
          <p className="mb-10 text-base text-muted-foreground sm:text-lg">
            Pick a course, or start with a single lesson. Every concept is explained step by step,
            at your pace.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to={START}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
            >
              Start Learning Free
              <ArrowRight className="h-5 w-5" aria-hidden />
            </Link>
            <a
              href={`mailto:${EDUCATOR_EMAIL}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-secondary px-7 py-3.5 text-base font-semibold text-foreground transition-colors hover:bg-accent"
            >
              I'm an educator
            </a>
          </div>
        </div>
      </section>

      {/* ─── Tricolor strip ─── */}
      <div className="flex" aria-hidden>
        <div className="h-px flex-1 bg-saffron" />
        <div className="h-px flex-1 bg-white" />
      </div>

      {/* ─── Footer ─── */}
      <SiteFooter width="max-w-6xl px-4 sm:px-6" />
    </div>
  );
}

/**
 * October: Cybersecurity Awareness Month, featuring the Spot the Scam course
 * and the 26-question challenge, with posters for campaigns.
 */
function AwarenessMonth() {
  const year = new Date().getFullYear();
  const api = useApi();
  // Titles and counts come from the live course and quiz (fallbacks while loading).
  const course = useQuery({
    queryKey: ['publicCourse', 'spot-the-scam'],
    queryFn: () => api.publicCourse('spot-the-scam'),
    retry: false,
  });
  const quiz = useQuery({
    queryKey: ['publicQuiz', 'cyber-month-2026'],
    queryFn: () => api.publicQuiz('cyber-month-2026'),
    retry: false,
  });
  // The Awareness Month video (content/videos awareness-month), between the intro and the picks.
  const awarenessVideo = findVideo('awareness-month');
  const lessons = course.data?.modules.reduce((n, m) => n + m.lessons.length, 0) ?? 17;
  const questions = quiz.data?.questionCount ?? 26;
  return (
    <section id="awareness" className="scroll-mt-4 px-4 pb-16 sm:px-6 md:pb-24">
      <div className="mx-auto max-w-5xl rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-india-green/10 p-6 sm:p-10 md:p-12">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary-strong sm:text-sm">
            <CalendarHeart className="h-4 w-4" aria-hidden />
            October {year} · Cybersecurity Awareness Month
          </div>
          <h2 className="mb-4 font-display text-2xl leading-tight font-semibold text-foreground sm:text-3xl md:text-4xl">
            Scams don't take a month off.{' '}
            <span className="text-india-green">Get ready this October.</span>
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            Cybersecurity Awareness Month is the moment to check your own habits and help the people
            around you stay safe. Start with two free picks: learn how today's scams work, then
            prove what you know.
          </p>
        </div>

        {awarenessVideo && (
          <div className="mx-auto mt-8 max-w-3xl md:mt-10">
            <VideoPlayer video={awarenessVideo} compact />
            <p className="mt-3 text-center text-sm font-medium text-foreground">
              It takes a village to build defenses for our digital world. Watch it, then share it
              with someone you love.
            </p>
          </div>
        )}

        <div className="mt-8 grid gap-5 md:mt-10 md:grid-cols-2">
          <Feature
            icon={ShieldAlert}
            kind="Free course"
            title={course.data?.title ?? "Spot the Scam: How Today's Scams Work and How to Recover"}
            description="How 16 of today's most common scams work, from AI voice clones and fake police calls to payment-app tricks and phishing, with the warning signs and what to do in the first hour if one gets through."
            facts={[`${lessons} lessons`, 'Self-paced', 'Certificate']}
            to={AWARENESS.course}
            cta="Start the course"
            posters={`${AWARENESS.course}/poster`}
            accent="text-india-green"
          />
          <Feature
            icon={ClipboardCheck}
            kind="Free challenge"
            title={quiz.data?.title ?? 'Cybersecurity Awareness Month 2026 Challenge'}
            description={`${questions} questions on the scams and threats everyone faces today: phishing, fake support calls, payment-app fraud, account takeovers and more. See where you stand and earn a certificate when you pass.`}
            facts={[
              `${questions} questions`,
              `About ${Math.round(questions / 5) * 5} minutes`,
              'Certificate',
            ]}
            to={AWARENESS.challenge}
            cta="Take the challenge"
            posters={`${AWARENESS.challenge}/poster`}
            accent="text-primary-strong"
          />
        </div>

        <p className="mt-8 flex items-start justify-center gap-2 text-center text-sm text-muted-foreground">
          <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-primary-strong" aria-hidden />
          <span>
            Running a campaign at your university or company? Both come with printable posters and a
            QR code, and organizations can assign the course to every member.
          </span>
        </p>
      </div>
    </section>
  );
}

function Feature({
  icon: Icon,
  kind,
  title,
  description,
  facts,
  to,
  cta,
  posters,
  accent,
}: {
  icon: LucideIcon;
  kind: string;
  title: string;
  description: string;
  facts: string[];
  to: string;
  cta: string;
  posters: string;
  accent: string;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card p-6 sm:p-7">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-muted">
          <Icon className={`h-6 w-6 ${accent}`} aria-hidden />
        </div>
        <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          {kind}
        </span>
      </div>
      <h3 className="mb-2 font-display text-lg font-semibold text-foreground sm:text-xl">
        {title}
      </h3>
      <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{description}</p>
      <p className="mb-6 text-xs font-medium text-muted-foreground">{facts.join(' · ')}</p>
      <div className="mt-auto flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to={to}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          {cta}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <Link
          to={posters}
          className="-my-2 py-2 text-center text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Get campaign posters
        </Link>
      </div>
    </div>
  );
}

/** How people can join: "Online", "In person", or both. */
const formatsLabel = (formats: SessionFormat[]) =>
  formats.length === 1 ? SESSION_FORMAT_LABELS[formats[0]!] : 'In person and online';

/** One session: its day and time. A series: when it starts (or its next session, once under way). */
function whenLabel(e: LibraryEvent) {
  if (e.sessionCount === 1) return formatSessionTime(e.startsAt, e.endsAt);
  const started = new Date(e.startsAt).getTime() < Date.now();
  const at = new Date(started ? e.nextStartsAt : e.startsAt);
  const day = at.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = at.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${started ? 'Next session' : 'Starts'} ${day} · ${time}`;
}

/**
 * Upcoming public workshops and webinars, Nirvik's and orgs', soonest first. Hidden
 * when none are scheduled. Times show in the visitor's own zone.
 */
function UpcomingEvents() {
  const api = useApi();
  const upcoming = useQuery({ queryKey: ['libraryEvents'], queryFn: api.libraryEvents });
  const events = upcoming.data?.events ?? [];
  if (!events.length) return null;
  return (
    <section id="events" className="mx-auto max-w-6xl scroll-mt-4 px-4 py-16 sm:px-6 md:py-20">
      <div className="mb-10 text-center md:mb-12">
        <p className="mb-3 text-xs font-bold tracking-[0.24em] text-saffron uppercase">
          Live and free
        </p>
        <h2 className="mb-4 font-display text-2xl font-semibold text-foreground sm:text-3xl md:text-4xl">
          Upcoming workshops and webinars
        </h2>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          Learn live, ask your questions, and bring your friends. Save your seat and we&rsquo;ll
          send a calendar invite and reminders.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
        {events.map((e, i) => {
          const style = CARD_STYLES[i % CARD_STYLES.length]!;
          const facts = [
            formatsLabel(e.formats),
            e.sessionCount > 1 ? `${e.sessionCount} sessions` : null,
            LEVEL_LABELS[e.level],
            e.certificate ? 'Certificate' : null,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div
              key={e.slug}
              className={`flex flex-col rounded-2xl border border-border bg-card p-6 transition-colors sm:p-7 ${style.ring}`}
            >
              <div className="mb-5 flex items-start justify-between gap-3">
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${style.iconBg}`}
                >
                  <CalendarDays className="h-6 w-6" aria-hidden />
                </div>
                <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                  {EVENT_KIND_LABELS[e.kind]}
                </span>
              </div>
              <h3 className="mb-2 font-display text-lg font-semibold text-foreground sm:text-xl">
                {e.title}
              </h3>
              <p className="mb-3 text-sm font-medium text-foreground">{whenLabel(e)}</p>
              {e.summary && (
                <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{e.summary}</p>
              )}
              {e.host ? (
                <OfferedBy org={e.host} label="Hosted by" className="mb-3" />
              ) : (
                <p className="mb-3 text-sm text-muted-foreground">Hosted by {e.hostName}</p>
              )}
              {e.hosts && (
                <p className="-mt-1 mb-3 text-sm text-muted-foreground">With {e.hosts}</p>
              )}
              <p className="mb-6 text-xs font-medium text-muted-foreground">
                {facts}
                {e.seatsLeft !== null &&
                  ` · ${e.seatsLeft ? `${e.seatsLeft} seat${e.seatsLeft === 1 ? '' : 's'} left` : 'Waitlist open'}`}
              </p>
              <div className="mt-auto flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Link
                  to={`/e/${e.slug}`}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Save my seat
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  to={`/e/${e.slug}/poster`}
                  className="-my-2 py-2 text-center text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Get posters to share
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Topic → icon for live course cards. */
const TOPIC_ICONS: Partial<Record<Topic, LucideIcon>> = {
  'scams-fraud': ShieldAlert,
  phishing: ShieldAlert,
  'security-basics': ShieldCheck,
  'app-security': Code2,
  'ai-security': Swords,
  'building-with-ai': Bot,
  'devices-networks': Globe,
  'security-operations': ShieldCheck,
};
const CARD_STYLES = [
  {
    iconBg: 'bg-india-green/15 text-india-green border-india-green/30',
    ring: 'hover:border-india-green/40',
  },
  {
    iconBg: 'bg-primary/15 text-primary-strong border-primary/30',
    ring: 'hover:border-primary/40',
  },
  {
    iconBg: 'bg-chakra-navy/15 text-chakra-navy border-chakra-navy/35',
    ring: 'hover:border-chakra-navy/45',
  },
  { iconBg: 'bg-saffron/15 text-saffron border-saffron/35', ring: 'hover:border-saffron/45' },
];

/** Tracks still being built: shown as "Coming soon". */
/**
 * The builder tracks still on the way, from basics to advanced (beginner,
 * then intermediate, then advanced; build priority within a level). Remove a
 * track here when its course is published: it then shows with the live
 * courses above, placed by LEARNING_PATH.
 */
const COMING_SOON: {
  icon: LucideIcon;
  title: string;
  description: string;
  length?: string;
  /** Hidden once a library course with this slug is live (for courses loaded as drafts). */
  slug?: string;
  /** Offered only to authorized teams, on request to this address. */
  restrictedContact?: string;
}[] = [
  {
    icon: Sparkles,
    title: 'Secure Vibe Coding',
    description:
      'For apps built with Lovable, Bolt, Replit, v0, Base44 and Firebase Studio. Secure what you prompted into existence: data rules, keys, sign-in, payments and launch checks, with a lesson for each platform.',
    length: 'Beginner',
    slug: 'secure-vibe-coding',
  },
  {
    icon: Bot,
    title: 'Securing AI-Generated Code and Coding Agents',
    description:
      'For builders using Cursor, Claude Code and Copilot. Review and test what AI writes, and keep coding agents on a short leash: permissions, secrets and safe workflows.',
    length: 'Beginner–Intermediate',
    slug: 'securing-ai-code',
  },
  {
    icon: Workflow,
    title: 'Secure Automations',
    description:
      'For Zapier, n8n, Make and Apps Script: credentials, OAuth scopes, webhooks, and the data your automations copy from app to app.',
    length: 'Beginner',
    slug: 'secure-automations',
  },
  {
    icon: Fingerprint,
    title: 'Privacy and Data Protection for Builders',
    description:
      'Collect less, store it safely, honor deletion requests and handle breaches: GDPR, India’s DPDP Act and US state laws in practice.',
    length: 'Beginner',
    slug: 'privacy-for-builders',
  },
  {
    icon: Siren,
    title: 'Your App Got Hacked: The First 24 Hours',
    description:
      'A calm, hour-by-hour plan for leaked keys, exposed data, hijacked accounts, phishing and payment fraud, including who to notify in the US and India.',
    length: 'Beginner',
    slug: 'first-24-hours',
  },
  {
    icon: ShieldCheck,
    title: 'Security Fundamentals for Builders',
    description:
      'Hands-on, for builders shipping code: sign-in and sessions, permissions, secrets, TLS and the most common vulnerabilities, with labs.',
    length: 'Beginner–Intermediate',
    slug: 'security-fundamentals',
  },
  {
    icon: BrainCircuit,
    title: 'Building Secure LLM Apps and Agents',
    description:
      'Adding chatbots, RAG or agents to your product? Defend against prompt injection, data leaking through retrieval, and agents with too much power.',
    length: 'Intermediate',
    slug: 'secure-llm-apps',
  },
  {
    icon: Cloud,
    title: 'Cloud Defense',
    description:
      'Understand and secure IAM, storage, networking and infrastructure as code across AWS, Google Cloud and Azure, with labs in disposable sandboxes.',
    length: 'Intermediate',
    slug: 'cloud-defense',
  },
  {
    icon: Swords,
    title: 'AI Red Team',
    description:
      'Prompt injection, model extraction and agentic exploit chains: how attackers turn frontier models into weapons, and how defenders stay ahead.',
    length: 'Advanced',
    restrictedContact: 'redteaming@nirvik.ai',
  },
];

/**
 * Live courses from basics to advanced: the learning path first, in order,
 * then any other course by level. The API lists them newest first.
 */
const LEARNING_PATH = [
  'spot-the-scam',
  'protect-your-family',
  'ai-at-work',
  'security-foundations',
  'secure-vibe-coding',
  'securing-ai-code',
  'secure-automations',
  'privacy-for-builders',
  'first-24-hours',
  'security-fundamentals',
  'cyber-security-master-class',
  'agentic-ai-workshop',
  'secure-llm-apps',
  'cloud-defense',
  'detection-engineering',
];
const LEVEL_RANK: Record<string, number> = { beginner: 0, intermediate: 1, advanced: 2 };
const pathRank = (slug: string, level: string) => {
  const i = LEARNING_PATH.indexOf(slug);
  return i >= 0 ? i : LEARNING_PATH.length + (LEVEL_RANK[level] ?? 1);
};

/** Live courses from Nirvik's library, then the tracks still on the way. */
/** The three learning paths, so visitors can see where they'd start. */
function StartingPoints() {
  return (
    <section id="paths" className="mx-auto max-w-6xl scroll-mt-4 px-4 pt-16 sm:px-6 md:pt-24">
      <div className="mb-10 text-center">
        <h2 className="mb-4 font-display text-2xl font-semibold text-foreground sm:text-3xl md:text-4xl">
          Find your starting point.
        </h2>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          Three paths through our courses, quizzes and workshops, by how much you already know. Pick
          the one that sounds like you.
        </p>
      </div>
      <PathCards />
      <p className="mt-6 flex flex-col items-center gap-2 text-center sm:flex-row sm:justify-center sm:gap-6">
        <Link
          to="/paths/start"
          className="-my-2 inline-flex items-center gap-1 py-2 text-sm font-semibold text-primary"
        >
          Not sure? Take the 2-minute quiz <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <Link
          to="/paths"
          className="-my-2 inline-flex items-center gap-1 py-2 text-sm font-semibold text-primary"
        >
          Compare the learning paths <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </p>
    </section>
  );
}

function Courses() {
  const api = useApi();
  const library = useQuery({ queryKey: ['libraryCourses'], queryFn: api.libraryCourses });
  const courses = [...(library.data?.courses ?? [])].sort(
    (a, b) => pathRank(a.slug, a.level) - pathRank(b.slug, b.level),
  );
  return (
    <section id="tracks" className="mx-auto max-w-6xl scroll-mt-4 px-4 py-16 sm:px-6 md:py-24">
      <div className="mb-12 text-center md:mb-14">
        <h2 className="mb-4 font-display text-2xl font-semibold text-foreground sm:text-3xl md:text-4xl">
          Tracks built for how builders build today.
        </h2>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          Self-paced, hands-on, and designed for the age of AI-assisted development.
        </p>
      </div>

      {library.isPending && (
        <p className="text-center text-sm text-muted-foreground">Loading courses…</p>
      )}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
        {courses.map((course, i) => {
          const style = CARD_STYLES[i % CARD_STYLES.length]!;
          const Icon = course.topics.map((t) => TOPIC_ICONS[t]).find(Boolean) ?? BookOpen;
          const length = [
            course.lessonCount
              ? `${course.lessonCount} lesson${course.lessonCount === 1 ? '' : 's'}`
              : null,
            formatMinutes(course.minutes),
            LEVEL_LABELS[course.level],
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Link
              key={course.slug}
              to={`/c/${course.slug}`}
              className={`group flex flex-col rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-0.5 sm:p-7 ${style.ring}`}
            >
              <div className="mb-5 flex items-start justify-between gap-3">
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${style.iconBg}`}
                >
                  <Icon className="h-6 w-6" aria-hidden />
                </div>
                <span className="rounded-full bg-muted px-2.5 py-1 text-right text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                  {length}
                </span>
              </div>
              <h3 className="mb-2 font-display text-lg font-semibold text-foreground sm:text-xl">
                {course.title}
              </h3>
              {course.summary && (
                <p className="text-sm leading-relaxed text-muted-foreground">{course.summary}</p>
              )}
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-strong">
                Start the course
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </span>
            </Link>
          );
        })}
      </div>

      {/* Coming soon tracks */}
      <div className="mt-10">
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase">
            Opening soon
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {COMING_SOON.filter((t) => !t.slug || !courses.some((c) => c.slug === t.slug)).map(
            (track) => (
              <div
                key={track.title}
                className="rounded-2xl border border-border/50 bg-muted/40 p-5 opacity-70"
              >
                <div className="mb-4 flex items-start justify-between gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-muted">
                    <track.icon className="h-5 w-5 text-muted-foreground" aria-hidden />
                  </div>
                  <span className="rounded-full border border-border/50 bg-muted px-2 py-1 text-[10px] font-bold tracking-wider text-muted-foreground/70 uppercase">
                    {track.restrictedContact ? 'Restricted' : 'Coming Soon'}
                  </span>
                </div>
                <h3 className="mb-1.5 font-display text-base font-semibold text-muted-foreground">
                  {track.title}
                </h3>
                {track.length && (
                  <p className="mb-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground/70 uppercase">
                    {track.length}
                  </p>
                )}
                <p className="text-xs leading-relaxed text-muted-foreground/70">
                  {track.description}
                </p>
                {track.restrictedContact && (
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                    Restricted: available only to authorized teams. To request access, email{' '}
                    <a
                      href={`mailto:${track.restrictedContact}`}
                      className="-my-2 inline-block py-2 font-semibold break-all text-foreground underline underline-offset-2"
                    >
                      {track.restrictedContact}
                    </a>
                    .
                  </p>
                )}
              </div>
            ),
          )}
        </div>
      </div>
    </section>
  );
}

function Bullets({ items, color }: { items: string[]; color: string }) {
  return (
    <ul className="mb-6 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
          <CheckCircle2 className={`mt-0.5 h-4.5 w-4.5 shrink-0 ${color}`} aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ContactButton({
  href,
  label,
  className,
  note = 'We reply within one business day.',
}: {
  href: string;
  label: string;
  className: string;
  /** The line under the button. */
  note?: string;
}) {
  return (
    <div className="shrink-0 md:min-w-[200px]">
      <a
        href={href}
        className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold transition-opacity hover:opacity-90 md:w-auto ${className}`}
      >
        {label}
        <ArrowRight className="h-5 w-5" aria-hidden />
      </a>
      <p className="mt-3 text-center text-xs text-muted-foreground md:text-left">{note}</p>
    </div>
  );
}

/** The pitch video (content/videos platform-pitch): what Nirvik Ready is, for anyone deciding. */
function PitchVideo() {
  const video = findVideo('platform-pitch');
  if (!video) return null;
  return (
    <section id="overview" className="mx-auto max-w-4xl scroll-mt-4 px-4 py-16 sm:px-6 md:py-20">
      <div className="mb-8 text-center">
        <h2 className="mb-3 font-display text-2xl font-semibold text-foreground sm:text-3xl md:text-4xl">
          See Nirvik Ready in four minutes
        </h2>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          What learners get, how organizations run it, and how it fits universities, non-profits,
          companies and communities.
        </p>
      </div>
      <VideoPlayer video={video} compact />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Link to="/videos" className="-my-2 py-2 font-semibold text-primary-strong hover:underline">
          More video tutorials
        </Link>
        {' · '}
        <Link
          to="/guides/organizations"
          className="-my-2 py-2 font-semibold text-primary-strong hover:underline"
        >
          Guide for organizations
        </Link>
      </p>
    </section>
  );
}

/**
 * A section for one kind of organization: its pitch, a call to action, and its
 * pitch video embedded beside it with links to more videos, its guide and Resources.
 */
function AudienceSection({
  id,
  card,
  chip,
  linkColor,
  icon: Icon,
  kicker,
  title,
  video,
  guide,
  action,
  children,
}: {
  id: string;
  /** Border and gradient classes for the card. */
  card: string;
  /** Classes for the kicker chip. */
  chip: string;
  linkColor: string;
  icon: LucideIcon;
  kicker: string;
  title: string;
  /** Slug of the pitch video (content/videos). */
  video: string;
  guide: { to: string; label: string };
  action: ReactNode;
  children: ReactNode;
}) {
  const v = findVideo(video);
  const link = `-my-2 inline-flex items-center gap-1.5 py-2 text-sm font-semibold hover:underline ${linkColor}`;
  return (
    <section id={id} className="mx-auto max-w-6xl scroll-mt-4 px-4 py-12 sm:px-6 md:py-16">
      <div className={`rounded-3xl border bg-gradient-to-br via-card p-6 sm:p-10 md:p-12 ${card}`}>
        <div className="grid gap-8 lg:grid-cols-2 lg:items-start lg:gap-12">
          <div>
            <div
              className={`mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium sm:text-sm ${chip}`}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {kicker}
            </div>
            <h2 className="mb-4 font-display text-2xl leading-tight font-semibold text-foreground sm:text-3xl md:text-4xl">
              {title}
            </h2>
            {children}
            {action}
          </div>
          <div className="space-y-4">
            {v && <VideoPlayer video={v} compact />}
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              <Link to="/videos" className={link}>
                <PlayCircle className="h-4 w-4 shrink-0" aria-hidden /> More videos
              </Link>
              <Link to={guide.to} className={link}>
                <BookOpen className="h-4 w-4 shrink-0" aria-hidden /> {guide.label}
              </Link>
              <Link to="/resources" className={link}>
                All resources
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
